import { cleanRegNo, validateRegistrationNumber } from '@utils/giCar/registrationValidation'
import logger from '@utils/logger'
import { getTurnstileToken, isTurnstileEnabledForProduct } from '@utils/turnstile/turnstile'
import axios from 'axios'

const CarInsuranceSubmission = async (
    rawNumber: string,
    regexPatternsData: any[],
    giCarContext: any,
    turnstileConfig: any,
    mobileNumber: string,
    onSuccess?: ((data: any) => void) | undefined,
    onError?: ((error: any) => void) | undefined,
    setLocalError?: (error: string) => void,
    setIsDirty?: (isDirty: boolean) => void,
    trackEvent?: (event: string, props: any) => unknown,
    getCommonEventProps?: (rawNumber: string) => any,
    source: 'cta' | 'enter' | 'auto' = 'cta',
) => {
    const normalizedNumber = cleanRegNo(rawNumber)
    const validation = validateRegistrationNumber(rawNumber, regexPatternsData)
    if (source === 'cta') {
        try {
          void trackEvent?.('tap_registration_number_cta', getCommonEventProps?.(rawNumber))
        } catch {
          // tracking should not block submission
        }
      }
    if (!validation.valid) {
        setLocalError?.(validation.error || '')
        giCarContext.setErrorMessage(validation.error || '')
        setIsDirty?.(true)

        if (source === 'cta') {
            try {
                if (trackEvent && getCommonEventProps) {
                    void trackEvent('error_triggered_on_cta', {
                        ...getCommonEventProps(rawNumber),
                        error_message: validation.error || '',
                        context: 'client error',
                    })
                }
            } catch {
                // tracking should not block submission
            }
        }

        onError?.({
            type: 'validation_error',
            message: validation.error,
        })
        return
    }
    try {
        giCarContext.setIsLoading(true)
        setLocalError?.('')
        const proposalAPIUrl = '/motororchestrator/api/v2/proposals'
        const CAR_PROPOSAL_TIMEOUT_MS = 15000
        const illogicalFlowConst = [
            'ILLOGICAL_FLOW_POLICY_EXISTS',
            'ILLOGICAL_FLOW_PAYMENT_DONE',
            'ILLOGICAL_FLOW_POLICY_EXPIRED_RENEWAL_DUE',
            'ILLOGICAL_FLOW_UNACCEPTABLE_ASSET',
        ]
        const shouldValidateTurnstile = isTurnstileEnabledForProduct(turnstileConfig, 'car')
        const turnstileToken = shouldValidateTurnstile && turnstileConfig
            ? await getTurnstileToken(turnstileConfig)
            : null
        await axios
            .request({
                method: 'post',
                url: proposalAPIUrl,
                headers: {
                    'X-Landing-URL': typeof window !== 'undefined' ? window.location?.href : '/',
                    'X-Landing-Path': '/',
                    'x-app-name': 'frontend-seo',
                    'x-platform': 'mweb',
                    ...(turnstileToken ? { 'x-turnstile-token': turnstileToken } : {}),
                },
                data: {
                    registration_number: normalizedNumber,
                    mobile_no: mobileNumber || '',
                    origin: 'acko',
                    product: 'car',
                    is_new: false,
                },
                timeout: CAR_PROPOSAL_TIMEOUT_MS,
            })
            .then(res => {
                if (res.data?.error_code) {
                    const isIllogicalFlow = illogicalFlowConst.filter(key => key === res.data?.error_code).length > 0

                    if (isIllogicalFlow) {
                        const rawErrorContext = res.data.error_context
                        const errorContext =
                            typeof rawErrorContext === 'object' && rawErrorContext !== null ? rawErrorContext : res.data
                        const detectedVehicleType =
                            typeof rawErrorContext === 'string'
                                ? rawErrorContext
                                : errorContext.vehicle_type || res.data.vehicle_type
                        const illogicalFlowData = {
                            error_code: res.data.error_code,
                            message: res.data.error_message || res.data.message || `Error: ${res.data.error_code}`,
                            registration_number: normalizedNumber,
                            user_logged_in: errorContext.user_logged_in ?? false,
                            last_digits: errorContext.last_digits || '',
                            redirect_url: errorContext.redirect_url || null,
                            vehicle_type: detectedVehicleType || 'car',
                            ...errorContext,
                            ...res.data,
                        }

                        giCarContext.setIllogicalFlowData(illogicalFlowData)
                        giCarContext.setProposalData(res.data)
                        onError?.({
                            type: 'illogical_flow',
                            error_code: res.data.error_code,
                            data: illogicalFlowData,
                        })
                    } else {
                        giCarContext.setErrorMessage(res.data.error_message || res.data.message || 'An error occurred')
                        onError?.({
                            type: 'api_error',
                            error_code: res.data.error_code,
                            data: res.data,
                        })
                    }
                } else if (res.data?.ekey) {
                    giCarContext.setProposalData(res.data)

                    onSuccess?.(res.data)
                } else {
                    giCarContext.setProposalData(res.data)
                    onSuccess?.(res.data)
                }
            })
            .catch(error => {
                logger.error('Error submitting registration number:', error)
                const err = JSON.stringify(error?.request?.response || error?.response?.data || {}).toUpperCase()
                if (err.includes('MODEL_UNSERVICEABLE')) {
                    const errorData = error?.response?.data || error?.request?.response || {}
                    const illogicalFlowData = {
                        error_code: 'MODEL_UNSERVICEABLE',
                        message: 'Model unserviceable',
                        registration_number: normalizedNumber,
                        user_logged_in: false,
                        last_digits: '',
                        redirect_url: null,
                        vehicle_type: 'car',
                        ...errorData,
                    }
                    giCarContext.setIllogicalFlowData(illogicalFlowData)
                    giCarContext.setProposalData({
                        error_code: 'MODEL_UNSERVICEABLE',
                        error_context: {
                            user_logged_in: false,
                            last_digits: '',
                            redirect_url: null,
                            vehicle_type: 'car',
                        },
                    })
                    onError?.({
                        type: 'illogical_flow',
                        error_code: 'MODEL_UNSERVICEABLE',
                        data: illogicalFlowData,
                    })
                } else if (error?.request?.status === 400) {
                    try {
                        const errorObject = JSON.parse(error?.request?.response || '{}')
                        const errorMessage = errorObject?.error_message || 'Invalid registration number. Please try again.'
                        setLocalError?.(errorMessage)
                        giCarContext.setErrorMessage(errorMessage)

                        onError?.({
                            type: 'api_error',
                            error: errorObject,
                        })
                    } catch (parseError) {
                        const errorMessage = 'Invalid registration number. Please try again.'
                        setLocalError?.(errorMessage)
                        giCarContext.setErrorMessage(errorMessage)
                        onError?.({
                            type: 'api_error',
                            error: { error_message: errorMessage },
                        })
                    }
                } else {
                    const errorMessage = error?.message || 'An error occurred. Please try again.'
                    setLocalError?.(errorMessage)
                    giCarContext.setErrorMessage(errorMessage)

                    onError?.({
                        type: 'exception',
                        error,
                    })
                }
            })
            .finally(() => {
                giCarContext.setIsLoading(false)
            })
    } catch (error: any) {
        logger.error('Unexpected error:', error)
        giCarContext.setIsLoading(false)
        const errorMessage = error?.message || 'An error occurred. Please try again.'
        setLocalError?.(errorMessage)
        giCarContext.setErrorMessage(errorMessage)

        onError?.({
            type: 'exception',
            error,
        })
    }
}

export default CarInsuranceSubmission
